import { Button, Col, DatePicker, Form, Input, Row, Select, SelectProps, Space } from 'antd';
import GenericTable from '../../../components/Commons/Table/generic-table.component';
import { ColumnProps } from "antd/es/table";
import ShowDataLength from '../../../components/Commons/Table/results-length-table.component';
import { useEffect, useState } from 'react';
import { getStatistics } from '../../../services/reports.service';
import { getAll as loadWorkers } from '../../../services/workers.service';
import { useQuery } from '@tanstack/react-query';
import { compareSortString, downloadExcel, formatCurrency, formatCurrencyToExport, formatDatePt } from '../../../services/utils';
import { RangePickerProps } from 'antd/es/date-picker';
import ExportBtn from '../../../components/Commons/Export/ExportButton';
import { isMobile } from 'react-device-detect';
import { ReportStatisticsResponse } from '../../../Interfaces/Reports.interfaces';
import FilterButton from '../../../components/Commons/Table/FilterButton';
import dayjs from 'dayjs';
import { EnumWorkersStatuses, EnumWorkersTypes } from '../../../Enums/WorkersTypes.enums';
import { WorkersResponse } from '../../../Interfaces/Workers.interfaces';

type ReportStatisticsLineKeyed = {
    key: number;
} & ReportStatisticsResponse['data'][0];

export default function Statistics() {
    const defaultMeta = {
        total_value: 0.0,
        total_profit: 0.0,
        count_services: 0,
        start_date: '',
        end_date: '',
    } satisfies ReportStatisticsResponse['meta'];
    
    const now = dayjs();
    
    const initialStartDate = now.subtract(1, 'w').format('YYYY-MM-DD');
    const initialEndDate = now.format('YYYY-MM-DD');
    
    const [workers, setWorkers] = useState<WorkersResponse<typeof EnumWorkersTypes.operators>['data']>([]);
    const [startDate, setStartDate] = useState<string>(initialStartDate);
    const [endDate, setEndDate] = useState<string>(initialEndDate);
    const [workerId, setWorkerId] = useState<number | string | undefined>(undefined);
    
    const [statistics, setStatistics] = useState<ReportStatisticsLineKeyed[]>([]);
    const [meta, setMeta] = useState<ReportStatisticsResponse['meta']>(defaultMeta);
    
    const [formFilter] = Form.useForm();
    
    const { data, isLoading: loading } = useQuery(
        ['getStatistics', startDate, endDate, workerId],
        () => getStatistics({
            start_date: startDate,
            end_date: endDate,
            operator_id: workerId,
        }),
        { refetchOnWindowFocus: false },
    );
    
    const { isLoading: workerLoading } = useQuery(
        ['loadWorkers'],
        () => loadWorkers({
            status: EnumWorkersStatuses.active,
            with_relationship: '0',
            type: EnumWorkersTypes.operators,
        }), {
            onSuccess: (response) => {
                setWorkers(response.data || []);
            },
            refetchOnWindowFocus: false
        }
    );
    
    useEffect(() => {
        if (data) {
            setStatistics(data.data.map((value, index) => ({
                key: index,
                ...value,
            }) satisfies ReportStatisticsLineKeyed));
            setMeta(data.meta);
        } else {
            setStatistics([]);
            setMeta(defaultMeta);
        }
    }, [data]);
    
    useEffect(() => {
        formFilter.setFieldsValue({
            start_date: dayjs(startDate ? startDate : initialStartDate),
            end_date: dayjs(endDate ? endDate : initialEndDate),
            operator_id: workerId || undefined,
        });
    }, [startDate, endDate, workerId]);
    
    const columns: ColumnProps<ReportStatisticsLineKeyed>[] = [
        {
            title: 'Data',
            dataIndex: 'date',
            key: 'date',
            width: 120,
            render: (text: string) => formatDatePt(text),
            sorter: {
                compare: (a, b) => Date.parse(a.date) - Date.parse(b.date),
                multiple: 3,
            },
            responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
        },
        {
            title: 'Zona',
            dataIndex: 'zone',
            key: 'zone',
            render: (text: string) => text || '—',
            sorter: {
                compare: (a, b) => compareSortString(a.zone, b.zone),
                multiple: 2,
            },
            responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
        },
        {
            title: 'Operador',
            dataIndex: 'operator',
            key: 'operator',
            render: (text: string) => text || '—',
            sorter: {
                compare: (a, b) => compareSortString(a.operator, b.operator),
                multiple: 1,
            },
            responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
        },
        {
            title: 'Valor serviços',
            dataIndex: 'value',
            key: 'value',
            width: 160,
            render: (value: number) => formatCurrency(value),
            sorter: (a, b) => a.value - b.value,
            responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
        },
        {
            title: 'Lucro',
            dataIndex: 'profit',
            key: 'profit',
            width: 120,
            render: (value: number) => formatCurrency(value),
            sorter: (a, b) => a.profit - b.profit,
            responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
        }
    ];
    
    const disabledStartDate: RangePickerProps['disabledDate'] = (current) => {
        let endDate = formFilter.getFieldValue('end_date');
        
        if (endDate) {
            return current >= endDate;
        }
        
        return false;
    };
    
    const disabledEndDate: RangePickerProps['disabledDate'] = (current) => {
        let startDate = formFilter.getFieldValue('start_date');
        
        if (startDate) {
            return current <= startDate;
        }
        
        return false;
    };
    
    const onClearFilters = () => {
        setStartDate('');
        setEndDate('');
        setWorkerId('');
    };
    
    const onFilter = () => {
        let start_date = formFilter.getFieldValue('start_date');
        let end_date = formFilter.getFieldValue('end_date');
        
        setStartDate(start_date ? start_date.format('YYYY-MM-DD') : '');
        setEndDate(end_date ? end_date.format('YYYY-MM-DD') : '');
        
        setWorkerId(formFilter.getFieldValue('operator_id'));
    };
    
    const filters = (
        <Form
            form={formFilter}
            name="filter_services"
            layout="vertical"
            className="form_filter_horizontal"
            autoComplete="off"
            style={isMobile ? {} : { width: 300 }}>
            <Row>
                <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }} xl={{ span: 24 }}>
                    <Form.Item label="Operador" name="operator_id" className="item_filter">
                        <Select
                            placeholder="Selecione o operador"
                            size="large"
                            style={{ width: '100%' }}
                            allowClear={true}
                            loading={workerLoading}
                            options={workers?.map((item) => ({
                                key: item.data.id,
                                value: item.data.id,
                                label: item.data.name,
                            })) as SelectProps['options']}
                        />
                    </Form.Item>
                </Col>
                
                <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }} xl={{ span: 24 }}>
                    <Form.Item name="start_date" label="Data início" className="item_filter">
                        <DatePicker size="large" placeholder="dd/mm/aaaa" format={"DD/MM/YYYY"}
                            style={{ width: '100%' }} disabledDate={disabledStartDate} value={initialStartDate} />
                    </Form.Item>
                </Col>
                
                <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }} xl={{ span: 24 }}>
                    <Form.Item name="end_date" label="Data fim" className="item_filter">
                        <DatePicker size="large" placeholder="dd/mm/aaaa" format={"DD/MM/YYYY"}
                            style={{ width: '100%' }} disabledDate={disabledEndDate} value={initialEndDate} />
                    </Form.Item>
                </Col>
                
                <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }} xl={{ span: 24 }}>
                    <Button onClick={onFilter} size="large">Filtrar</Button>
                    
                    {(startDate !== initialStartDate || endDate !== initialEndDate || workerId)
                        ? <Button type="link" onClick={onClearFilters} size="large">Limpar</Button>
                        : ''
                    }
                </Col>
            </Row>
        </Form>
    );
    
    const onMapDataToExport = () => {
        const dados = statistics.map((record) => {
            return {
                'Data': formatDatePt(record.date),
                'Zona': record.zone,
                'Operador': record.operator,
                'Valor serviços': formatCurrencyToExport(record.value || 0),
                'Lucro': formatCurrencyToExport(record.profit || 0),
            };
        });

        return dados.concat([{
            'Data': meta.count_services + ' serviço(s)',
            'Zona': '',
            'Operador': 'TOTAL',
            'Valor serviços': formatCurrencyToExport(meta.total_value),
            'Lucro': formatCurrencyToExport(meta.total_profit),
        }]);
    };

    return (
        <>
            <div className="table">
                <GenericTable
                    columns={columns}
                    dataSource={statistics}
                    loading={loading}
                    totalChildren={
                        <div className="zone_result_size_n_export" style={isMobile ? { marginTop: 10 } : {}}>
                            <ShowDataLength size={meta.count_services} className="show_result_size" />
                            
                            <Space direction={isMobile ? 'vertical' : 'horizontal'} className="show_result_size">
                                <Form.Item label="Valor total do período" className='item_filter'
                                    style={isMobile ? { width: '100%' } : { width: 250 }}>
                                    <Input placeholder='Valor total' readOnly value={formatCurrency(meta.total_value)} />
                                </Form.Item>
                                
                                <FilterButton filters={filters} />
                                
                                <ExportBtn disabled={loading || !statistics.length} loading={loading}
                                    onClick={() => {
                                        // https://stackoverflow.com/questions/1976007/what-characters-are-forbidden-in-windows-and-linux-directory-names
                                        let regex = /(?:[\<\>\:\"\/\\\|\?\*\s\-\0-\x1F]+)/g;
                                        
                                        let filename = 'Estatisticas';
                                        
                                        if(workerId && workers) {
                                            let currentWorker = null;
                                            
                                            for(let worker of workers) {
                                                if(worker.data.id === workerId) {
                                                    currentWorker = worker;
                                                }
                                            }
                                            
                                            if(currentWorker) {
                                                filename += ' - ' + currentWorker.data.name.replaceAll(regex, ' ');
                                            }
                                        }
                                        
                                        filename += ' - '
                                            + formatDatePt(meta.start_date).replaceAll(regex, '-')
                                            + ' a '
                                            + formatDatePt(meta.end_date).replaceAll(regex, '-');
                                        
                                        downloadExcel(onMapDataToExport(), filename.trim().replace(/\.$/, ''));
                                    }} />
                            </Space>
                        </div>
                    }
                    empty={{
                        createTheFirst: true,
                        textOne: 'Nenhumas estatisticas disponíveis',
                        textTwo: startDate !== initialStartDate || endDate !== initialEndDate || workerId
                            ? 'Tente novamente com diferentes valores de filtro.'
                            : '',
                        link: '',
                        createText: ''
                    }}
                    scroll={isMobile ? { x: 800 } : undefined}
                />
            </div>
        </>
    );
}